import { Injectable, signal } from '@angular/core';
import { WorkspaceApiService } from '../api';
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Workspace, WorkspacePayload, WorkspaceUpdatePayload } from '../models';
import { SubscriblyStatusService } from './subscribly-status.service';

/**
 * Store that manages all user workspaces including the one that is currently active.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  /**
   * All user workspaces by account id
   */
  userWorkspacesByAccountId = new Map<string, Workspace[]>();

  /**
   * Currently active / visible workspace
   */
  activeWorkspace = signal<Workspace>({} as Workspace);

  constructor(
    private workspaceApi: WorkspaceApiService,
    private subscriblyStatus: SubscriblyStatusService,
  ) {}

  /**
   * Fetches all user workspaces by account id from the API and sets it in the store.
   * Channel details are polled from subscrib.ly; if that poll fails, workspaces
   * are loaded from Doddly without those details.
   */
  getWorkspacesByAccountId(accountId: string): Observable<Workspace[]> {
    return this.subscriblyStatus
      .withFallback(
        () => this.workspaceApi.getAllWorkspaces(accountId, true),
        () => this.workspaceApi.getAllWorkspaces(accountId, false),
      )
      .pipe(
        map((workspaces) =>
          workspaces.map((workspace) => ({
            ...workspace,
            channels: workspace.channels ?? [],
          })),
        ),
        tap((workspaces) => {
          this.userWorkspacesByAccountId.set(accountId, workspaces);
        }),
      );
  }

  /**
   *
   * Creates new workspace
   *
   */
  createWorkspace(payload: WorkspacePayload): Observable<Workspace> {
    return this.workspaceApi.createWorkspace(payload).pipe(
      tap((newWorkspace) => {
        this.userWorkspacesByAccountId.set(newWorkspace.accountId, [
          ...(this.userWorkspacesByAccountId.get(newWorkspace.accountId) ?? []),
          newWorkspace,
        ]);
      }),
    );
  }

  /**
   *
   * Deletes workspace
   *
   */
  deleteWorkspace(workspaceId: string): Observable<Workspace> {
    return this.workspaceApi.deleteWorkspace(workspaceId).pipe(
      tap((deletedWorkspace) => {
        this.userWorkspacesByAccountId.set(
          deletedWorkspace.accountId,
          this.userWorkspacesByAccountId
            .get(deletedWorkspace.accountId)
            ?.filter((workspace) => workspace.id !== deletedWorkspace.id) ?? [],
        );
      }),
    );
  }

  /**
   *
   * Updates workspace
   *
   */
  updateWorkspace(
    workspaceId: string,
    payload: WorkspaceUpdatePayload,
  ): Observable<Workspace> {
    return this.workspaceApi.updateWorkspace(workspaceId, payload).pipe(
      tap((updatedWorkspace) => {
        this.userWorkspacesByAccountId.set(
          updatedWorkspace.accountId,
          this.userWorkspacesByAccountId
            .get(updatedWorkspace.accountId)
            ?.map((workspace) =>
              workspace.id === updatedWorkspace.id
                ? updatedWorkspace
                : workspace,
            ) ?? [],
        );
      }),
    );
  }
}
